import * as React from 'react'
import { Button } from 'react-bootstrap'
import Form, { IChangeEvent, ISubmitEvent } from 'react-jsonschema-form'
import ActionsLocalService from '../Services/ActionsLocalService'
import HttpActionsService from '../Services/HttpActionsService'
import LocalizationStore from '../Stores/LocalizationStore'
import ScheduleConfigurationStore from '../Stores/ScheduleConfigurationStore'
import UserStore from '../Stores/UserStore'
import { PaymentScheduleItemModel } from './PaymentScheduleItemModel'
import { Questionnaire, QuestionnaireStatus } from './Questionnaire'
import { IScheduleConfig, ScheduleConfig } from './ScheduleConfig'

export interface ICreditCondition {
    id: string
    termCountMonths: number
    creditPercentPerMonth: number
    creditPercentDiscountPerMonth: number
    paymentsScheduleJson: Array<PaymentScheduleItemModel>
    prepaymentSum: number
    percentPenaltyPerDay: number
    isProgressiveAlgorithm: boolean
    progressiveConfigId: string
}

export class CreditCondition {

    constructor() {
        this.paymentsScheduleJson = []
        this.prepaymentSum = 0
    }

    id: string
    termCountMonths: number
    creditPercentPerMonth: number
    creditPercentDiscountPerMonth: number
    paymentsScheduleJson: Array<PaymentScheduleItemModel>
    prepaymentSum: number
    percentPenaltyPerDay: number
    isProgressiveAlgorithm: boolean
    progressiveConfigId: string
}

export function getCreditconditionFormScheme(condition: ICreditCondition): any {
    const ls = LocalizationStore.getInstance()
    const scheduleConfigurationStore = ScheduleConfigurationStore.getInstance()
    let config: IScheduleConfig[];

    config = scheduleConfigurationStore.getConfig && scheduleConfigurationStore.getConfig!['scheduleConfigurations'] ?
        scheduleConfigurationStore.getConfig!['scheduleConfigurations'] : [new ScheduleConfig()]

    let defaultscheduleConfiguration = config.filter(x => x.id === condition.progressiveConfigId).length ?
        config.filter(x => x.id === condition.progressiveConfigId)[0] :
        config.filter(x => x.id === "default-config-calculatePrepayment-true")[0]

    return {
        title: ls.t!('creditCondition'),
        type: 'object',
        required: [
            'termCountMonths',
            'prepaymentSum',
            'creditPercentPerMonth',
            'creditPercentDiscountPerMonth',
            'percentPenaltyPerDay',
        ],
        properties: {
            termCountMonths: {
                title: ls.t!('termCountMonths'),
                type: 'integer',
                minimum: 1,
                maximum: 100,
                default: 6
            },
            prepaymentSum: {
                title: ls.t!('prepaymentSum'),
                type: 'number',
                minimum: 0,
                default: 0
            },
            creditPercentPerMonth: {
                title: ls.t!('creditPercentPerMonth'),
                type: 'number',
                minimum: 0,
                default: 1.5
            },
            creditPercentDiscountPerMonth: {
                title: ls.t!('creditPercentDiscountPerMonth'),
                type: 'number',
                minimum: 0,
                default: 0
            },
            percentPenaltyPerDay: {
                title: ls.t('percentPenaltyPerDay'),
                type: 'number',
                minimum: 0,
                default: 0.8
            },
            isProgressiveAlgorithm: {
                title: ls.t!('isProgressiveAlgorithm'),
                type: 'boolean',
            },
            progressiveConfigId: {
                title: `${ls.t('selectProgressiveConfig')}`,
                type: 'string',
                enum: ['', ...config.map(x => x.id)],
                enumNames: ['', ...config.map(x =>
                    `${x.id} ${x.values.filter(y=>y.length == condition.termCountMonths)}`
                )],
                default: defaultscheduleConfiguration ? defaultscheduleConfiguration.id : '',
                uniqueItems: true
            },


            // isProgressiveAlgorithmBlock: {
            //     Header: ls!.t(''),
            //     maxWidth: 150,
            //     minWidth: 90,
            //     accessor: '',
            //     filterable: false,
            //     sortable: false,
            //     Cell: (cellInfo: CellInfo) =>
            //         (
            //             <div>
            //                 isProgressiveAlgorithmBlock
            //                 {/* <Button onClick={() => {
            //                     let httpActionsService = HttpActionsService.getInstance()
            //                     httpActionsService.getScheduleConfiguration()
            //                 }}>
            //                     LOAD
            //                 </Button> */}
            //             </div>
            //         )

            // }
        }
    }
}

export function getCreditconditionFormUISchema(isReadonly: boolean, isProgressiveAlgorithm: boolean) {

    if(!isProgressiveAlgorithm) { 
        return {
            termCountMonths: {
                'ui:readonly': isReadonly
            },
            prepaymentSum: {
                'ui:readonly': isReadonly
            },
            creditPercentPerMonth: {
                'ui:readonly': isReadonly
            },
            creditPercentDiscountPerMonth: {
                'ui:readonly': isReadonly
            },
            percentPenaltyPerDay: {
                'ui:readonly': isReadonly
            },
            isProgressiveAlgorithm: {
                'ui:readonly': isReadonly
            },
            progressiveConfigId: {
                'ui:widget': 'hidden'
            }
        }
    }

    return {
        termCountMonths: {
            'ui:readonly': isReadonly
        },
        prepaymentSum: {
            'ui:readonly': isReadonly
        },
        creditPercentPerMonth: {
            'ui:readonly': isReadonly
        },
        creditPercentDiscountPerMonth: {
            'ui:readonly': isReadonly
        },
        percentPenaltyPerDay: {
            'ui:readonly': isReadonly
        },
        isProgressiveAlgorithm: {
            'ui:readonly': isReadonly
        }
    }
}

export function onChange(e: IChangeEvent<ICreditCondition>) {
    if (e.formData) {
        console.log(e.formData);

        ActionsLocalService.getInstance().updateCreditConditionInQ(e.formData)
    }
}

export function onSubmit(e: ISubmitEvent<ICreditCondition>) {
    delete e.formData.paymentsScheduleJson
    HttpActionsService.getInstance()
        .updateCreditConditions(e.formData)
}

export function checkCanSave(questionnaire: Questionnaire) {
    const userStore = UserStore.getInstance()

    return (
        questionnaire.id &&
        questionnaire.status &&
        questionnaire.status !== 0 &&
        questionnaire.status !== QuestionnaireStatus.Canceled &&
        (
            userStore.checkAdmin
            || userStore.checkSuperAdmin
            || userStore.checkCreditGroupAdministrator
            || userStore.checkCollectionGroupAdministrator
            || (
                userStore.id === questionnaire.inProgressByUserId
                && (
                    (userStore.checkCreditConsultant &&
                        questionnaire.status === QuestionnaireStatus.InProgressCreditConsultant)
                    || (userStore.checkCreditExpert &&
                        questionnaire.status === QuestionnaireStatus.InProgressCreditExpert)
                )
            )
        )
    )
}

export function getCreditConditionForm(questionnaire: Questionnaire) {
    const ls = LocalizationStore.getInstance()
    let isReadonly = !checkCanSave(questionnaire)

    return (
        <Form
            schema={getCreditconditionFormScheme(questionnaire!.credit!.creditCondition)}
            uiSchema={getCreditconditionFormUISchema(isReadonly && !!questionnaire.id, questionnaire.credit.creditCondition.isProgressiveAlgorithm)}
            onSubmit={onSubmit}
            formData={questionnaire!.credit!.creditCondition}
            onChange={onChange}
        >
            <div
                className="questionnaireBtns"
                hidden={isReadonly}
            >
                <Button
                    className="w-100"
                    type="submit"
                >
                    {ls!.t!('Save')}
                </Button>
            </div>
        </Form >
    )
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Model/CreditCondition.tsx